import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Actions } from '@src/app/shared/backend/auth/Action';
import { FilterRequestForCombo, RetailersService, UnregisteredUsersService } from '@src/app/shared/backend/clients';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { FiltersPanelChangeEvent, FiltersPanelDropdownLazyLoadEvent, FiltersPanelFieldOverrides } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { buildRemoteDropdownRequest, normalizeDropdownState } from '@src/app/shared/components/filters-panel/filters-panel-dropdown.helpers';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';

@Component({
    selector: 'app-unregistered-users',
    templateUrl: './unregistered-users.component.html',
    styleUrl: './unregistered-users.component.scss',
    host: {
        class: 'app-unregistered-users'
    },
    standalone: false
})
export class UnregisteredUsersComponent extends BaseListComponent implements OnInit, OnDestroy {

  private initialLoadDone = false;
  private readonly componentDestroyed$ = new Subject<void>();

  public readonly unregisteredUsersTableName = TableSettingService.UnregisteredUsersTableName;

  public filtersPanelFieldOverrides: FiltersPanelFieldOverrides = {};

  // Dropdown: retailers
  retailers = {
    results: [],
    pageIndex: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false
  };

  currentDate: Date = new Date();
  allowNewUnregisteredUser: boolean = false;

  constructor(
    private router: Router,
    public permissionService: PermissionService,
    public override tableSettingService: TableSettingService,
    private translateService: TranslateService,
    private toasterService: ToasterService,
    private unregisteredUsersService: UnregisteredUsersService,
    private retailersService: RetailersService,
  ) {
    super();
  }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.restorePaginatorStateFromStorage();

    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.rebuildSortPanelColumnLabels();
      });

    await this.tableSettingService.initUnregisteredUsers();
    await super.setCache(this.unregisteredUsersTableName);

    this.allowNewUnregisteredUser = await this.permissionService.hasActionPermissions(Actions.create_users);

    this.initSortPanel({
      defaultSort: { key: 'firstName', direction: 'ASC' },
      labelResolver: (col) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        if (translated && translated !== translationKey) {
          return translated;
        }

        return col.header;
      }
    });

    this.rebuildSortPanelColumnLabels();

    // Fallback: if the panel doesn't restore anything, load unfiltered!!
    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.get();
      }
    });
  }

  override async get() {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.page = await firstValueFrom(this.unregisteredUsersService.apiUnregisteredUsersFilterTablePost(this.filter));
    this.data = this.page.results;
  }

  override onPageChange(event: any): void {
    this.initialLoadDone = true;
    super.onPageChange(event);
  }

  protected override onCriteriaResetRequested(): void {
    this.resetPaginatorStorage();
  }

  private resetPaginatorStorage(): void {
    resetPaginatorStateToFirstPage({
      tableName: this.unregisteredUsersTableName,
      pageSize: this.pageSize,
    });
  }

  private restorePaginatorStateFromStorage(): void {
    const stored = loadStoredPaginatorState(this.unregisteredUsersTableName);

    if (!stored) {
      return;
    }

    const storedPageNumber = stored.valuesByFieldKey.pageNumber;
    const storedPageSize = stored.valuesByFieldKey.pageSize;

    this.pageNumber = storedPageNumber;
    this.pageSize = storedPageSize;

    this.filter.pageNumber = storedPageNumber;
    this.filter.pageSize = storedPageSize;
  }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent): Promise<void> {
    // Important: pass all table cols (not only visible ones) to prevent hidden col filters from affecting results
    const currentFilters = this.filter.filters as any;

    const nextFilters = applyFiltersPanelChange(
      evt,
      currentFilters,
      this.allTableColumns
    );

    if (!nextFilters) {
      return;
    }

    this.filter.filters = nextFilters as any;

    const source = evt.source;
    const isApplyAction = source === 'apply';
    const isClearAction = source === 'clear';
    const shouldResetPaging = isApplyAction || isClearAction;

    if (shouldResetPaging) {
      const firstPageNumber = 1;

      this.pageNumber = firstPageNumber;
      this.filter.pageNumber = firstPageNumber;

      this.onCriteriaResetRequested();
    }

    this.initialLoadDone = true;
    await this.get();
  }

  async onFiltersPanelDropdownLazyLoad(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
    if (!evt) return;

    const handlers: Record<string, (e: FiltersPanelDropdownLazyLoadEvent) => Promise<void>> = {
      retailerIds: (e) => this.loadRetailersCombo(e),
    };

    const handler = handlers[evt.fieldKey];
    if (!handler) return;

    await handler(evt);
  }

  private async loadRetailersCombo(evt: FiltersPanelDropdownLazyLoadEvent): Promise<void> {
    const requestPayload: FilterRequestForCombo = buildRemoteDropdownRequest({
      pageIndex: evt.pageIndex,
      pageSize: evt.pageSize,
      search: evt.search
    });

    const response = await firstValueFrom(
      this.retailersService.apiRetailersFilterComboPost(requestPayload)
    );

    const normalized = normalizeDropdownState(response as any, evt.pageIndex) as any;

    const results = Array.isArray(normalized?.results) ? normalized.results : [];
    normalized.results = results.map((retailer: any) => ({
      id: retailer?.id,
      text: retailer?.text ?? retailer?.name ?? retailer?.label ?? `#${retailer?.id}`
    }));

    this.retailers = normalized;
    this.rebuildFiltersPanelOverrides();
  }

  private rebuildFiltersPanelOverrides(): void {
    this.filtersPanelFieldOverrides = {
      retailerIds: {
        fieldLabel: 'TABLE.HEADER.Retailers',
        isMultiselect: true,

        options: this.retailers.results ?? [],
        optionLabel: 'text',
        optionValue: 'id',

        placeholder: 'Select...',
        searchPlaceholder: 'Search...',
        searchMode: 'remote',

        showSearchbox: true,
        showClear: true,
        compactUi: true,

        pageSize: 10,
        totalCount: this.retailers.totalCount ?? 0,
        totalPages: this.retailers.totalPages ?? 1,
        pageIndex: this.retailers.pageIndex ?? 1,
        hasPreviousPage: this.retailers.hasPreviousPage ?? false,
        hasNextPage: this.retailers.hasNextPage ?? false
      }
    };
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    const nextSortableColumns = visibleColumns
      .filter((col: any) => {
        const field = String(col?.field ?? '').trim();
        return (
          field.length > 0 &&
          field.toLowerCase() !== 'actions' &&
          col?.sortable !== false
        );
      })
      .map((col: any) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        let label;
        if (translated && translated !== translationKey) {
          label = translated;
        } else {
          label = col.header;
        }

        return {
          key: col.field,
          label
        };
      });

    this.sortableColumns = nextSortableColumns;

    const allowedKeys = new Set(nextSortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    }
  }

  goToCreateUnregisteredUserForm() {
    this.router.navigate(['/unregistered-users/add']);
  }

  goToUnregisteredUserDetails(event: any, unregisteredUserId: number) {
    this.router.navigate([`/unregistered-users/${unregisteredUserId}`]);
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
